// Result-slot shaping for the gallery (docs/plan/phase-10-ui.md). A Task's
// results are a flat, deterministic list of slots, each carrying a
// `slot: Record<dimension, index>` coordinate (08-contracts.md #1,
// internal/core/tasks.go:80-91). "1:1" and "1:N" are not two different
// data shapes -- they are two ways of reading the same coordinates, so
// both views are built from these functions rather than from two branches
// in the component.
import type { ResultSlot } from "../api/client";

/** The dimension names present across a result set, e.g. ["image","seed"].
 * Sorted for stability: Core sorts slots lexicographically by dimension too
 * (tasks.go:809-822), so the first dimension here is the outer loop there. */
export function slotDims(slots: ResultSlot[]): string[] {
  const dims = new Set<string>();
  for (const s of slots) for (const d of Object.keys(s.slot ?? {})) dims.add(d);
  return Array.from(dims).sort();
}

export interface SlotGroup {
  /** The dimension index this group was taken from (an input photo's slot
   * number for a photo_to_video task). */
  index: number;
  slots: ResultSlot[];
}

/** Group slots by one dimension, in index order -- the "1:N" reading: one
 * input photo, N results under it. Slots missing that dimension land in
 * the group for index -1 so nothing is ever silently dropped from the
 * gallery. */
export function groupSlots(slots: ResultSlot[], dim: string): SlotGroup[] {
  const groups = new Map<number, ResultSlot[]>();
  for (const s of slots) {
    const raw = s.slot?.[dim];
    const index = typeof raw === "number" ? raw : -1;
    const list = groups.get(index);
    if (list) list.push(s);
    else groups.set(index, [s]);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a - b)
    .map(([index, groupSlots]) => ({ index, slots: groupSlots }));
}

/** A slot's coordinate as a label. Scoped to one dimension when a header
 * already names it, so a grouped tile reads "variant 3" instead of
 * "image 0, variant 3". */
export function slotLabel(slot: ResultSlot, dims?: string[]): string {
  const keys = (dims ?? Object.keys(slot.slot ?? {})).filter((d) => d in (slot.slot ?? {}));
  const parts = keys.sort().map((dim) => `${dim} ${slot.slot[dim]}`);
  return parts.join(", ") || "result";
}

/** The dimension to group by by default: the first coordinate dimension
 * (Core orders slots by it), or undefined for a task with no coordinates
 * at all. */
export function defaultGroupDim(slots: ResultSlot[]): string | undefined {
  return slotDims(slots)[0];
}

export function isVideo(contentType?: string | null): boolean {
  return !!contentType && contentType.startsWith("video/");
}

const EXT_BY_TYPE: Record<string, string> = {
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "application/json": "json",
};

/** Extension for a downloaded artifact: the one its content type implies,
 * or "bin" for a type we have no mapping for. A batch download is useless
 * if every file lands with no extension, so this is worth being explicit
 * about. */
export function extensionFor(contentType?: string | null): string {
  if (!contentType) return "bin";
  return EXT_BY_TYPE[contentType.split(";")[0].trim().toLowerCase()] ?? "bin";
}

/** `task-1a2b3c4d/image0-seed3.mp4` -- readable, sortable, and unique per
 * slot, which is what a zip of 200 results needs to be. */
export function resultFileName(
  taskId: string,
  slot: ResultSlot,
  contentType?: string | null,
  dims?: string[],
): string {
  const dir = `task-${taskId.slice(0, 8)}`;
  const keys = (dims && dims.length ? dims : Object.keys(slot.slot ?? {})).slice().sort();
  const parts = keys
    .filter((d) => d in (slot.slot ?? {}))
    .map((d) => `${d}${slot.slot[d]}`)
    .join("-");
  return `${dir}/${parts || "result"}.${extensionFor(contentType)}`;
}

/** The name to hand a browser for a *single* file: the same name as inside
 * the zip, flattened. `resultFileName` uses `/` to mean "inside this
 * folder" in the archive, but a `download` attribute has no folders -- the
 * browser decides for itself what to do with a slash, and what it decides
 * is not a name anyone chose. */
export function downloadFileName(name: string): string {
  return name.replace(/\//g, "-");
}

/** Sum of artifact sizes where Core has reported them, and how many are
 * still unknown (a just-committed artifact's size arrives with the commit
 * notification, so early rows can be null). Both numbers are shown: a
 * batch total that silently omits half the batch is worse than one
 * labelled "at least". */
export function totalBytes(sizes: (number | undefined | null)[]): { known: number; unknown: number } {
  let known = 0;
  let unknown = 0;
  for (const s of sizes) {
    if (typeof s === "number" && s > 0) known += s;
    else unknown += 1;
  }
  return { known, unknown };
}

/** Human-readable byte size for a batch total. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** The shareable form of a result (ADR-0019): its download URL with the
 * read token in the query string, which Store accepts for reads
 * (08-contracts.md #4). Read tokens do not expire by default, so this link
 * lives as long as the artifact does, and deleting the artifact is what
 * revokes it. Undefined until Core has handed out a token (the artifact is
 * not COMMITTED yet, or the view has not loaded). The absolute
 * download_url is used as given -- STORE_PUBLIC_URL is the public address
 * of Store -- not re-targeted at this page's origin the way api/store.ts
 * does for its own fetches. */
export function shareLink(view?: { download_url?: string; download_token?: string } | null): string | undefined {
  if (!view?.download_url || !view.download_token) return undefined;
  const sep = view.download_url.includes("?") ? "&" : "?";
  return `${view.download_url}${sep}t=${encodeURIComponent(view.download_token)}`;
}
